// Alignment step component - Step 5 of character creation wizard

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

const ALIGNMENTS = [
  {
    slug: 'lawful-good',
    name: 'Lawful Good',
    abbreviation: 'LG',
    description: 'Creatures that act with compassion, honor, and a sense of duty.',
  },
  {
    slug: 'neutral-good',
    name: 'Neutral Good',
    abbreviation: 'NG',
    description: 'Creatures that do the best they can to help others according to their needs.',
  },
  {
    slug: 'chaotic-good',
    name: 'Chaotic Good',
    abbreviation: 'CG',
    description: 'Creatures that act as their conscience directs with little regard for what others expect.',
  },
  {
    slug: 'lawful-neutral',
    name: 'Lawful Neutral',
    abbreviation: 'LN',
    description: 'Creatures that act in accordance with law, tradition, or personal codes.',
  },
  {
    slug: 'neutral',
    name: 'Neutral',
    abbreviation: 'N',
    description: "Creatures that prefer to steer clear of moral questions and don't take sides.",
  },
  {
    slug: 'chaotic-neutral',
    name: 'Chaotic Neutral',
    abbreviation: 'CN',
    description: 'Creatures that follow their whims, holding personal freedom above all else.',
  },
  {
    slug: 'lawful-evil',
    name: 'Lawful Evil',
    abbreviation: 'LE',
    description: 'Creatures that methodically take what they want within the limits of a code of tradition or loyalty.',
  },
  {
    slug: 'neutral-evil',
    name: 'Neutral Evil',
    abbreviation: 'NE',
    description: 'Creatures that do whatever they can get away with, without compassion or qualms.',
  },
  {
    slug: 'chaotic-evil',
    name: 'Chaotic Evil',
    abbreviation: 'CE',
    description: 'Creatures that act with arbitrary violence, spurred by greed, hatred, or bloodlust.',
  },
];

/**
 * Alignment selection step component.
 * Controlled component: receives all data as properties, emits events for mutations.
 *
 * Events: select-alignment
 */
class AlignmentStep extends BaseComponent {
  static properties = {
    selectedSlug: { type: String, attribute: 'selected-slug' },
    recommendedAlignment: { type: String, attribute: 'recommended-alignment' },
    isHeaderStuck: { type: Boolean, attribute: 'is-header-stuck' },
  };

  static styles = css`
    :host {
      display: block;
    }

    .selectable-card {
      cursor: pointer;
      transition:
        border-color 0.15s,
        box-shadow 0.15s;
    }

    .selectable-card:hover:not(.disabled) {
      border-color: #0d6efd;
    }

    .selectable-card.selected {
      border-color: #0d6efd;
      box-shadow: 0 0 0 1px #0d6efd;
      background-color: rgba(13, 110, 253, 0.04);
    }

    .alignment-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 0.75rem;
    }

    .alignment-card {
      border: 1px solid rgba(0, 0, 0, 0.125);
      border-radius: 0.375rem;
      padding: 0.75rem;
    }

    @media (max-width: 768px) {
      .alignment-grid {
        grid-template-columns: repeat(2, 1fr);
      }
    }

    @media (max-width: 480px) {
      .alignment-grid {
        grid-template-columns: 1fr;
      }
    }

    .sticky-header {
      position: sticky;
      top: 0;
      background-color: rgba(255, 255, 255, 0.5);
      backdrop-filter: blur(8px);
      z-index: 10;
      padding-top: 0.5rem;
      padding-bottom: 1rem;
      margin-top: -0.5rem;
      margin-left: -1rem;
      margin-right: -1rem;
      padding-left: 1rem;
      padding-right: 1rem;
      border-bottom: 1px solid transparent;
      transition: border-color 0.15s ease;
    }

    .sticky-header.stuck {
      border-bottom-color: rgba(0, 0, 0, 0.125);
      padding-top: 1rem;
    }
  `;

  constructor() {
    super();
    this.selectedSlug = '';
    this.recommendedAlignment = '';
    this.isHeaderStuck = false;
  }

  render() {
    return html`
      <div class="sticky-header ${this.isHeaderStuck ? 'stuck' : ''}">
        <h5 class="card-title mb-0">Choose Your Alignment</h5>
      </div>
      <p class="text-muted mb-3">Alignment represents your character's moral and ethical outlook.</p>

      <div class="alignment-grid">
        ${ALIGNMENTS.map(
          (alignment) => html`
            <div
              class="selectable-card alignment-card ${this.selectedSlug === alignment.slug ? 'selected' : ''}"
              @click="${() => this._onSelect(alignment.slug)}"
            >
              <div>
                <span class="fw-medium">${alignment.name}</span>
                <span class="badge bg-secondary ms-2">${alignment.abbreviation}</span>
                ${
                  alignment.slug === this.recommendedAlignment
                    ? html`<span class="badge bg-primary bg-opacity-25 text-primary ms-2">Recommended</span>`
                    : ''
                }
              </div>
              <p class="mb-0 text-muted small mt-1">${alignment.description}</p>
            </div>
          `,
        )}
      </div>
    `;
  }

  /** @param {string} slug */
  _onSelect(slug) {
    this.dispatchEvent(new CustomEvent('select-alignment', { detail: { slug }, bubbles: true, composed: true }));
  }
}

customElements.define('alignment-step', AlignmentStep);
